import React, { useEffect } from 'react';

import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { Payload } from '@atlaskit/rovo-triggers/types';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { buildToolFromAction } from '../../../common/utils/buildToolFromAction';
import { AgentDiffCard } from '../agent-diff-card';

const disabledActions: (MessageActionInvocationStatus | undefined)[] = [
	'finished',
	'cancelled',
	'errored',
];

type UpdateAgentBaseProps = {
	actionRenderer: ActionRendererFor<
		| typeof ActionKeys.UpdateAgentConfigurationTool
		| typeof ActionKeys.StudioWorkspaceUpdateAgentDraft
	>;
	payload: Payload;
};

export const UpdateAgentBase = ({
	actionRenderer,
	payload,
}: UpdateAgentBaseProps): React.JSX.Element => {
	di(useAIMateExperienceTracker);

	const {
		actions,
		onCancel,
		getStatus,
		onCustomDataConfirm,
		cloudId,
		productKey,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	} = actionRenderer;

	const publish = usePublish('ai-mate');

	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		experienceTracker.start({
			id: ExperienceName.STUDIO_UPDATE_AGENT,
			name: ExperienceName.STUDIO_UPDATE_AGENT,
		});
	}, [experienceTracker]);

	const firstAction = actions[0];
	const { data } = firstAction;

	const status = getStatus(firstAction);

	const isDisabled = status !== 'idle' || disabledActions.includes(firstAction.status);
	const isSubmitting = status === 'started';

	const onClick = async () => {
		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'updateAgentRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', data)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
		} else {
			await onCustomDataConfirm(firstAction, data);
		}

		publish(payload);
	};

	return (
		<AgentDiffCard
			onConfirm={onClick}
			onCancel={() => onCancel(firstAction)}
			isDisabled={isDisabled}
			isSubmitting={isSubmitting}
			fields={data.fields}
			cloudId={cloudId}
			productKey={productKey}
			experienceTracker={experienceTracker}
		/>
	);
};
